import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { AlertCircle, TrendingUp, FileText, Music, FileSpreadsheet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface LabResult {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string | null;
  created_at: string;
  subcategory_id: string | null;
  source_type: string | null;
  source_session_id: string | null;
  source_file_name?: string;
  source_content_type?: string;
  subcategory_name?: string;
}

interface Subcategory {
  id: string;
  name: string;
  category_id: string;
  category_name?: string;
}

interface LabResultsTableProps {
  results: LabResult[];
  subcategories: Subcategory[];
  onRefetch: () => void;
}

export function LabResultsTable({ results, subcategories, onRefetch }: LabResultsTableProps) {
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const getStatus = (result: LabResult): "normal" | "high" | "low" | null => {
    if (!result.value || (!result.ref_low && !result.ref_high)) return null;
    
    if (result.ref_low && result.value < result.ref_low) return "low";
    if (result.ref_high && result.value > result.ref_high) return "high";
    return "normal";
  };

  const getStatusBadge = (status: "normal" | "high" | "low" | null) => {
    if (!status) return null;
    
    if (status === "high" || status === "low") {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Fora do range
        </Badge>
      );
    }
    
    return <Badge variant="outline" className="text-green-600">Normal</Badge>;
  };

  const getSourceIcon = (contentType?: string) => {
    if (!contentType) return <FileText className="h-4 w-4" />;
    if (contentType.includes("audio")) return <Music className="h-4 w-4" />;
    if (contentType.includes("spreadsheet") || contentType.includes("excel")) return <FileSpreadsheet className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const handleUpdateSubcategory = async (resultId: string, subcategoryId: string | null) => {
    setUpdatingIds(prev => new Set(prev).add(resultId));
    
    try {
      const { error } = await supabase
        .from("lab_results_onboarding")
        .update({ subcategory_id: subcategoryId })
        .eq("id", resultId);

      if (error) throw error;

      toast.success("Categoria atualizada");
      onRefetch();
    } catch (error) {
      console.error("Error updating subcategory:", error);
      toast.error("Erro ao atualizar categoria");
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(resultId);
        return next;
      });
    }
  };

  const handleNavigateToSource = (result: LabResult) => {
    if (result.source_type === 'manual_entry') {
      return;
    }
    
    if (result.source_session_id) {
      navigate(`/app/smartfile/${result.source_session_id}`);
    }
  };

  if (results.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p>Nenhum resultado laboratorial encontrado.</p>
      </div>
    );
  }

  // Agrupar subcategorias por categoria
  const groupedSubcategories = subcategories.reduce((acc, sub) => {
    const categoryName = sub.category_name || "Outras";
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(sub);
    return acc;
  }, {} as Record<string, Subcategory[]>);

  return (
    <>
      <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Exame</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead>Referência</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Origem</TableHead>
            <TableHead className="w-32 text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((result) => {
            return (
              <TableRow key={result.id} className="group">
                <TableCell className="font-medium">
                  {result.collected_at ? (
                    new Date(result.collected_at).toLocaleDateString("pt-BR")
                  ) : (
                    <span className="text-muted-foreground text-xs">Não informado</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{result.test_name}</div>
                  {result.source_type && (
                    <div className="text-xs text-muted-foreground capitalize">
                      {result.source_type.replace("_", " ")}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {result.value !== null ? (
                    <span className="font-semibold">
                      {result.value} {result.unit || ""}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {result.ref_low || result.ref_high ? (
                    <>
                      {result.ref_low && <span>{result.ref_low}</span>}
                      {result.ref_low && result.ref_high && <span> - </span>}
                      {result.ref_high && <span>{result.ref_high}</span>}
                      {result.unit && <span className="ml-1">{result.unit}</span>}
                    </>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    value={result.subcategory_id || "none"}
                    onValueChange={(value) => 
                      handleUpdateSubcategory(result.id, value === "none" ? null : value)
                    }
                    disabled={updatingIds.has(result.id)}
                  >
                    <SelectTrigger className="w-[200px] h-8 text-xs">
                      <SelectValue placeholder="Sem categoria" />
                    </SelectTrigger>
                    <SelectContent className="z-[100] max-h-[300px]">
                      <SelectItem value="none">Sem categoria</SelectItem>
                      {Object.entries(groupedSubcategories).map(([categoryName, subs]) => (
                        <SelectGroup key={categoryName}>
                          <SelectLabel className="text-xs font-semibold text-muted-foreground">
                            {categoryName}
                          </SelectLabel>
                          {subs.map((sub) => (
                            <SelectItem key={sub.id} value={sub.id} className="pl-6">
                              {sub.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {result.source_type === 'manual_entry' ? (
                    <Badge variant="outline" className="text-xs">Manual</Badge>
                  ) : result.source_file_name ? (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-xs flex items-center gap-1"
                      onClick={() => handleNavigateToSource(result)}
                    >
                      {getSourceIcon(result.source_content_type)}
                      <span className="truncate max-w-[150px]">
                        {result.source_file_name}
                      </span>
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </>
  );
}
